import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, t as translate } from '../i18n/translations';

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Try to get from localStorage first
    const stored = localStorage.getItem('trackvibes_dj_lang');
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }
    // Then browser language
    const browserLang = (navigator.language || 'fr').slice(0, 2);
    if (SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }
    return DEFAULT_LANGUAGE;
  });

  // Update localStorage when language changes
  const setLanguage = useCallback((newLang) => {
    if (SUPPORTED_LANGUAGES.includes(newLang)) {
      setLanguageState(newLang);
      localStorage.setItem('trackvibes_dj_lang', newLang);
    }
  }, []);

  // Sync with user profile language when user logs in
  const syncWithUserLanguage = useCallback((userLang) => {
    if (userLang && SUPPORTED_LANGUAGES.includes(userLang)) {
      setLanguage(userLang);
    }
  }, [setLanguage]);

  // Translation helper bound to current language
  const t = useCallback((section, key) => {
    return translate(language, section, key);
  }, [language]);

  const value = {
    language,
    setLanguage,
    syncWithUserLanguage,
    t,
    SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
